import { useState } from "react";
import { marketingStrategySchema, type MarketingStrategyPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function MarketingStrategy() {
  const [form, setForm] = useState<Partial<MarketingStrategyPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Заявка пришла мне лично. Свяжусь и согласую брифинг." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = marketingStrategySchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("MARKETING_STRATEGY", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Маркетинг — стратегия"
        title="Документ, по которому работает весь маркетинг"
        subtitle="4 встречи. На выходе — Google-документ, который можно передавать любым подрядчикам."
        icon="📋"
        iconColor="purple"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            В 95% случаев бизнесу не хватает именно маркетинг-стратегии.
            Хаос в маркетинге — это не «у нас плохой таргетолог», это отсутствие документа,
            где зафиксированы цели, аудитория, каналы, тактика и метрики.
          </Lead>
        </Card>

        <Card>
          <H3>Что получаешь</H3>
          <P>
            Подробный Google-документ, который можно передать любым подрядчикам — таргетологам,
            дизайнерам, контент-менеджерам, разработчикам сайта.
            Им не нужно будет додумывать, как делать сайт под твой продукт или на кого настраивать рекламу.
          </P>
        </Card>

        <Card>
          <H3>Работаем по системе SOSTAC</H3>
          <P>
            S — Situation. O — Objectives. S — Strategy. T — Tactic. A — Action. C — Control.
            Международная методология маркетингового планирования.
          </P>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-blue-bg)] text-[var(--icon-blue)]">1</div>
            <H3>Situation + Objectives</H3>
          </div>
          <P>Глубокий брифинг: где сейчас маркетинг и куда нужно прийти. На выходе — заполненный подробный бриф.</P>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-purple-bg)] text-[var(--icon-purple)]">2</div>
            <H3>Strategy + Tactic</H3>
          </div>
          <P>
            Разбираем ЦА: сегменты, потребности, мотивы покупки. Строим CJM — путь клиента от осознания проблемы
            до повторных покупок. На выходе: сегменты ЦА, позиционирование под каждый сегмент, карта CJM.
          </P>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-green-bg)] text-[var(--icon-green)]">3</div>
            <H3>Action</H3>
          </div>
          <P>Детальный план действий с декомпозицией на мелкие задачи и назначенными ответственными.</P>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-orange-bg)] text-[var(--icon-orange)]">4</div>
            <H3>Control</H3>
          </div>
          <P>После внедрения части плана — корректировки и усиления. Это не просто документ «в стол», а живая стратегия.</P>
        </Card>

        <Card>
          <H3>Кому подойдёт</H3>
          <CheckList items={[
            "Уже работающий бизнес с выручкой",
            "Есть маркетолог или подрядчики, которым нужен чёткий план",
            "Готовность выделить ресурс на внедрение",
          ]} />
        </Card>

        <div className="ios-callout-muted">
          Стоимость пакета — по заявке.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Ниша / продукт" error={errors["niche"]}>
          <input className={`ios-input ${errors["niche"] ? "ios-input-error" : ""}`}
            value={form.niche ?? ""} onChange={(e) => setForm({ ...form, niche: e.target.value })} />
        </Field>
        <Field label="Месячный оборот / размер бизнеса" error={errors["monthlyRevenue"]}>
          <input className={`ios-input ${errors["monthlyRevenue"] ? "ios-input-error" : ""}`}
            value={form.monthlyRevenue ?? ""} onChange={(e) => setForm({ ...form, monthlyRevenue: e.target.value })} />
        </Field>
        <Field label="Запрос" error={errors["request"]}>
          <textarea rows={4} className={`ios-input ${errors["request"] ? "ios-input-error" : ""}`}
            value={form.request ?? ""} onChange={(e) => setForm({ ...form, request: e.target.value })}
            placeholder="чего хочешь достичь стратегией?" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
